import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export interface ISubscriptionItem {
  business_id?: string;
  business_name?: string;
  client_email?: string;
  status?: string;
}

export interface ILinkedSubAccountsModalProps {
  open: boolean;
  handleClose: () => void;
  subscriptions?: ISubscriptionItem[];
  activeCount?: number;
  message?: string;
}

const LinkedSubAccountsModal: React.FC<ILinkedSubAccountsModalProps> = ({
  open,
  handleClose,
  subscriptions = [],
  activeCount,
  message,
}) => {
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          padding: 1,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontFamily: "var(--font-family-manrope)",
          fontWeight: 700,
          fontSize: "26px",
          color: "#111827",
          p: "20px 24px 12px",
        }}
      >
        Linked Sub-Accounts
        <IconButton
          onClick={handleClose}
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            color: "#64748b",
            p: 0,
            "&:hover": { bgcolor: "#f1f5f9", color: "#111827" },
          }}
          size="small"
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography
          sx={{
            fontFamily: "var(--font-family-manrope)",
            fontSize: "16px",
            color: "#4B5563",
            lineHeight: "24px",
            mb: 2.5,
          }}
        >
          {message ||
            `Cannot disconnect Stripe while active resold subscriptions exist. (Active Count: ${
              activeCount ?? subscriptions.length
            })`}
        </Typography>

        <Box
          sx={{
            border: "1px solid #E5E7EB",
            borderRadius: "var(--small-border-radius, 8px)",
            overflow: "hidden",
          }}
        >
          <Table>
            <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
              <TableRow>
                <TableCell
                  sx={{
                    fontFamily: "var(--font-family-manrope)",
                    fontWeight: 600,
                    fontSize: "13px",
                    color: "#374151",
                  }}
                >
                  Client Email
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: "var(--font-family-manrope)",
                    fontWeight: 600,
                    fontSize: "13px",
                    color: "#374151",
                  }}
                >
                  Business Name
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: "var(--font-family-manrope)",
                    fontWeight: 600,
                    fontSize: "13px",
                    color: "#374151",
                  }}
                >
                  Status
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscriptions.length > 0 ? (
                subscriptions.map((sub, index) => (
                  <TableRow key={sub.business_id || index}>
                    <TableCell
                      sx={{
                        fontFamily: "var(--font-family-manrope)",
                        fontSize: "14px",
                        color: "#111827",
                      }}
                    >
                      {sub.client_email || "N/A"}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "var(--font-family-manrope)",
                        fontSize: "14px",
                        color: "#111827",
                      }}
                    >
                      {sub.business_name || sub.business_id || "N/A"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={sub.status || "active"}
                        size="small"
                        sx={{
                          textTransform: "capitalize",
                          fontWeight: 500,
                          backgroundColor:
                            sub.status === "active" ? "#dcfce7" : "#fee2e2",
                          color:
                            sub.status === "active" ? "#227447" : "#991b1b",
                          borderRadius: "var(--small-border-radius, 6px)",
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                    <Typography
                      sx={{
                        fontFamily: "var(--font-family-manrope)",
                        fontSize: "14px",
                        color: "#9CA3AF",
                      }}
                    >
                      No active sub-account subscriptions found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            onClick={handleClose}
            sx={{
              backgroundColor: "var(--action-button-background-color, #1a73e8)",
              color: "#FFF",
              textTransform: "none",
              fontFamily: "var(--font-family-manrope)",
              fontWeight: 500,
              padding: "8px 24px",
              borderRadius: "var(--action-button-radius, 10px)",
              "&:hover": {
                backgroundColor: "var(--action-button-background-color, #1a73e8)",
                opacity: 0.9,
              },
            }}
          >
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LinkedSubAccountsModal;
